import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EuroIcon from '@mui/icons-material/Euro';
import BlockIcon from '@mui/icons-material/Block';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/subscriptionService';
import { ActivityWindowStats } from '../../types';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}

const StatCard = ({ label, value, icon, color = 'primary.main' }: StatCardProps) => (
  <Card elevation={2} sx={{ borderRadius: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={800}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ActivityTable = ({ data, t }: { data: ActivityWindowStats; t: (k: string) => string }) => (
  <Table size="small">
    <TableBody>
      <TableRow>
        <TableCell sx={{ color: 'text.secondary', border: 0 }}>{t('admin.stats.activityConsultations')}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, border: 0 }}>{data.consultations}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ color: 'text.secondary', border: 0 }}>{t('admin.stats.activityChat')}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, border: 0 }}>{data.chat_messages}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ color: 'text.secondary', border: 0 }}>{t('admin.stats.activitySuggestions')}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, border: 0 }}>{data.ai_suggestions}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ color: 'text.secondary', border: 0 }}>{t('admin.stats.activityDocuments')}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, border: 0 }}>{data.documents}</TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

export const AdminStatsPage = () => {
  const { t } = useTranslation();
  const [activityWindow, setActivityWindow] = useState<'1d' | '7d' | '30d'>('7d');

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: adminService.getStats,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {t('admin.loadError')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {t('admin.stats.title')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label={t('admin.stats.totalUsers')}
            value={stats.total_users}
            icon={<PeopleIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label={t('admin.stats.activeSubscriptions')}
            value={stats.active_subscriptions}
            icon={<CheckCircleIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label={t('admin.stats.mrr')}
            value={`${stats.mrr_eur.toFixed(0)}€`}
            icon={<EuroIcon />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label={t('admin.stats.trialing')}
            value={stats.trialing}
            icon={<HourglassEmptyIcon />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label={t('admin.stats.pendingTrial')}
            value={stats.pending_trial}
            icon={<HourglassEmptyIcon />}
            color="text.secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label={t('admin.stats.banned')}
            value={stats.banned}
            icon={<BlockIcon />}
            color="error.main"
          />
        </Grid>
      </Grid>

      {/* Activity Overview */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('admin.stats.activityTitle')}
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            {(['1d', '7d', '30d'] as const).map((w) => (
              <Button
                key={w}
                variant={activityWindow === w ? 'contained' : 'outlined'}
                onClick={() => setActivityWindow(w)}
              >
                {t(`admin.stats.window_${w}`)}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <ActivityTable
              data={
                activityWindow === '1d' ? stats.activity_1d
                : activityWindow === '7d' ? stats.activity_7d
                : stats.activity_30d
              }
              t={t}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
